import React from "react";
import { Link } from "react-router-dom";
import { Star, Instagram, MapPin } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { waLink } from "../lib/constants";

const FOOTER_LINKS = [
  { label: "Início", to: "/" },
  { label: "Barbearia", to: "/barbearia" },
  { label: "Clube", to: "/clube" },
  { label: "Prótese Capilar", to: "/protese-capilar" },
  { label: "Resultados", to: "/resultados" },
  { label: "Unidades", to: "/unidades" },
  { label: "Sobre", to: "/sobre" },
  { label: "Contato", to: "/contato" },
  { label: "Política de Privacidade", to: "/politica-de-privacidade" },
  { label: "Termos de Uso", to: "/termos-de-uso" },
];

export default function Footer() {
  const { site } = useSite();
  const socials = site?.socials || {};

  return (
    <footer className="bg-[#050505] border-t border-white/10 pt-16 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-5">
            {socials && site?.logo_barbearia ? (
              <img src={site.logo_barbearia} alt="Barbearia das Estrelas" className="h-16 w-auto object-contain" />
            ) : (
              <>
                <Star className="w-5 h-5 text-gold fill-gold" />
                <span className="font-heading font-600 text-white text-lg">
                  BARBEARIA <span className="text-gold">DAS ESTRELAS</span>
                </span>
              </>
            )}
          </div>
          <p className="text-zinc-400 max-w-md leading-relaxed text-sm">
            Barbearia, cuidado masculino e soluções capilares em um só lugar.
          </p>
          <div className="mt-6 space-y-2 text-sm text-zinc-400">
            <p><span className="text-white font-600">Prime Capilar Clinic</span> — especializada em prótese capilar.</p>
            <p>Atendimento de prótese capilar em Maceió em <span className="text-gold">parceria com a Arena Barbearia</span>.</p>
          </div>
          <div className="flex items-center gap-3 mt-6">
            {socials.instagram_barbearia && (
              <a href={socials.instagram_barbearia} target="_blank" rel="noreferrer" className="p-2.5 border border-white/15 rounded-sm text-zinc-300 hover:text-gold hover:border-gold transition-colors" data-testid="footer-instagram">
                <Instagram className="w-4 h-4" />
              </a>
            )}
            <a href={waLink(site, "barbearia")} target="_blank" rel="noreferrer" className="p-2.5 border border-white/15 rounded-sm text-zinc-300 hover:text-gold hover:border-gold transition-colors" data-testid="footer-whatsapp">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
            {socials.google && (
              <a href={socials.google} target="_blank" rel="noreferrer" className="p-2.5 border border-white/15 rounded-sm text-zinc-300 hover:text-gold hover:border-gold transition-colors" data-testid="footer-google">
                <MapPin className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div>
          <h4 className="overline-label text-xs text-gold mb-4">Navegação</h4>
          <ul className="space-y-2.5">
            {FOOTER_LINKS.slice(0, 6).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="overline-label text-xs text-gold mb-4">Institucional</h4>
          <ul className="space-y-2.5">
            {FOOTER_LINKS.slice(6).map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
          <div className="mt-6 text-sm text-zinc-500 space-y-1">
            <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" /> Arapiraca, AL</p>
            <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" /> Garanhuns, PE</p>
            <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" /> Maceió, AL</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-3">
        <p className="text-xs text-zinc-600">© {new Date().getFullYear()} Barbearia das Estrelas · Prime Capilar Clinic. Todos os direitos reservados.</p>
        <Link to="/admin" className="text-xs text-zinc-700 hover:text-zinc-500 transition-colors" data-testid="footer-admin-link">Painel administrativo</Link>
      </div>
    </footer>
  );
}
